import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Space, DatePicker, Form, Card, Spin } from 'antd';
import dayjs from 'dayjs';
import { supabase } from '../../supabaseClient';

const NodeList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});
  const [form] = Form.useForm();
  const [filtering, setFiltering] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // 查询和筛选
  const fetchNodes = async (filters?: any, page = 1, pageSize = 15) => {
    setLoading(true);
    let query = supabase.from('u_user_nodes').select('*', { count: 'exact' }).order('created_at', { ascending: false });
    if (filters) {
      if (filters.user_id) query = query.eq('user_id', filters.user_id);
      if (filters.start && filters.end) query = query.gte('created_at', filters.start).lte('created_at', filters.end);
    }
    query = query.range((page - 1) * pageSize, page * pageSize - 1);
    const { data: nodes, count } = await query;
    // 批量查邮箱
    let emailMap: Record<string, string> = {};
    let filteredNodes = nodes || [];
    if (filteredNodes.length > 0) {
      const userIds = Array.from(new Set(filteredNodes.map((n: any) => n.user_id)));
      const { data: users } = await supabase
        .from('u_profiles')
        .select('user_id,email')
        .in('user_id', userIds);
      if (users) {
        users.forEach((u: any) => {
          emailMap[u.user_id] = u.email;
        });
      }
      // 邮箱筛选（前端过滤）
      if (filters && filters.email) {
        filteredNodes = filteredNodes.filter((n: any) => {
          const email = emailMap[n.user_id] || '';
          return email.includes(filters.email);
        });
      }
    }
    setEmailMap(emailMap);
    setData(filteredNodes);
    setPagination({ current: page, pageSize, total: count || 0 });
    setLoading(false);
  };

  const handleFilter = async () => {
    setFiltering(true);
    const values = await form.validateFields();
    let start = dateRange && dateRange[0] ? dateRange[0] : undefined;
    let end = dateRange && dateRange[1] ? dateRange[1] : undefined;
    await fetchNodes({
      user_id: values.user_id || undefined,
      email: values.email || undefined,
      start,
      end,
    }, 1, pagination.pageSize);
    setFiltering(false);
  };

  useEffect(() => {
    fetchNodes();
  }, []);

  const handleTableChange = (pag: any) => {
    fetchNodes(form.getFieldsValue(), pag.current, pag.pageSize);
  };

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 120, ellipsis: true, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '单位', dataIndex: 'token_symbol', key: 'token_symbol', width: 80, ellipsis: true },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 280, ellipsis: true },
    { title: '用户邮箱', dataIndex: 'user_email', key: 'user_email', width: 220, ellipsis: true, render: (_: any, record: any) => emailMap[record.user_id] || '' },
    { title: '总额', dataIndex: 'total_amount', key: 'total_amount', width: 100, ellipsis: true },
    { title: '锁定', dataIndex: 'locked_amount', key: 'locked_amount', width: 100, ellipsis: true },
  ];

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Form form={form} layout="inline" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Form.Item name="user_id">
          <Input placeholder="用户ID" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="email">
          <Input placeholder="用户邮箱" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="created_at" style={{ marginBottom: 8 }}>
          <DatePicker.RangePicker
            style={{ width: 320 }}
            placeholder={['开始时间', '结束时间']}
            value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
            onChange={dates => setDateRange(dates && dates[0] && dates[1] ? [dates[0].toISOString(), dates[1].toISOString()] : null)}
            allowClear
            showTime={false}
          />
        </Form.Item>
        <Button type="primary" onClick={handleFilter} loading={filtering}>筛选</Button>
        <Button style={{ marginLeft: 8 }} onClick={() => { form.resetFields(); setDateRange(null); fetchNodes(); }}>重置</Button>
      </Form>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ ...pagination, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1200 }}
        size="small"
        style={{ minWidth: 800 }}
        tableLayout="fixed"
        onChange={handleTableChange}
      />
    </div>
  );
};

export default NodeList;
